import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { inputCls, UnitInput } from '../shared/FormKit';
import { FieldCommentInline } from './fieldComments';
import { FieldInvalidInline, useFieldInvalid } from '../shared/formValidation';
import { fieldInvalidLabel, coverageMeta } from './constants';
import { formatDate } from '../../utils/dateFormat';
import { fetchJSON } from '../../utils/api';
import { QcTip, QcTipProvider, QcTipAllToggle } from './QcForm';
import NowName from '../shared/NowName';
import { parseEngagementName, engagementTag } from '../../utils/engagementName';
import Qc35TimeGuideline from './Qc35TimeGuideline';
import {
  getQc35Items, isQc35ItemShown, applyQc35AutoAnswers, fmtQc35AnswerValue,
  isQc35DerivedItem, qc35DerivedValue, qc35CommentItemOf, isQc35ItemLocked,
  isQc35ItemRequired, qc35GuidelineOf, qc35PercentError,
} from '../../pages/qc35Questions';

/**
 * Form QC35 (Revised) — File Archival Form, laid out like the firm's Word form
 * (bank: src/pages/qc35Questions.js). The checklist IS the QC35 — there is no
 * document to upload; the approved form prints from these answers.
 *
 * Header: the firm name is an ANSWER (defaults to PKF-CAP LLP, editable);
 * Client Name and Reporting Year-end are read from the covered engagements
 * and never typed again. Checklist rows are typed by the submitter — except
 * the bank's `derived` rows, which read live off the covered engagement the
 * same way the header does (Date of Audit Report = the Signed FS record's
 * signing date). The fee-recovery explanation row exists only while the rate
 * is below 30% (bank `showWhen`).
 *
 * The three time-involvement rows carry a Comments box inside their Response
 * cell (the QC6 / QC7 procedure-row look: a small caption over each part)
 * and print the QMM guideline that applies to this engagement under the
 * value. Which guideline applies, whether the EQR hours are required or
 * locked "Not applicable", and whether a Comments box has become required
 * all come from the engagement's QC context — fetched here through
 * useQc35Context when the primary engagement changes, and handed back to the
 * page through `onContext` so its gap list uses the same rules.
 *
 * `Qc35ChecklistForm` edits, `Qc35ChecklistView` reads; the same table serves
 * the submitter (WorkflowQc35New), the reviewer (WorkflowQc35Detail), the
 * Modify modal, the QC35 Amendment (New / Detail) and the Management record
 * form (recordKinds). The approved form prints from the same bank
 * (server/services/qc35FormPdf.js).
 *
 * `engagements` — slim rows of the covered engagements (id, entityName /
 * engagementName, serviceProvided / engagementSeq / serviceDescription,
 * yearEnd where known).
 */

// ── QC context (risk rating + EQR of the covered engagement's QC form) ──────
const contextCache = new Map();

/**
 * The QC context for an engagement — null while unknown (no engagement yet,
 * still loading, or no QC record). One fetch per engagement per page load.
 */
export const useQc35Context = (engagementId) => {
  const key = engagementId ? String(engagementId) : null;
  const [state, setState] = useState(() => ({ key, ctx: key && contextCache.has(key) ? contextCache.get(key) : null }));
  useEffect(() => {
    if (!key) { setState({ key: null, ctx: null }); return undefined; }
    if (contextCache.has(key)) { setState({ key, ctx: contextCache.get(key) }); return undefined; }
    let alive = true;
    setState({ key, ctx: null });
    (async () => {
      try {
        const data = await fetchJSON(`/api/workflow/qc35/qc-context/${key}`);
        const ctx = data.context || null;
        contextCache.set(key, ctx);
        if (alive) setState({ key, ctx });
      } catch {
        // No context ⇒ the bank's lower guideline applies and the EQR row
        // stays optional; the server still validates with the real one.
        if (alive) setState({ key, ctx: null });
      }
    })();
    return () => { alive = false; };
  }, [key]);
  return state.key === key ? state.ctx : null;
};

// Table cell classes — same look as QcForm's Word-form tables.
const cellLabel = 'w-[46%] px-3 py-2 text-sm text-slate-800 bg-slate-50 border border-slate-300 align-top';
const cellValue = 'px-3 py-2 border border-slate-300 align-top';
const colHead = 'px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 bg-slate-100 border border-slate-300';
// The small caption over a part of a two-part cell (Response · Comments);
// red while Submit found that part empty (formValidation) — QcForm's subCaption.
const subCaption = (invalid) => `text-[11px] font-semibold uppercase tracking-wide ${invalid ? fieldInvalidLabel : 'text-slate-500'} mb-1`;

const HeaderRow = ({ label, children }) => (
  <tr>
    <th scope="row" className="w-48 px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 bg-slate-50 border border-slate-300 align-top">{label}</th>
    <td className="px-3 py-1.5 text-sm text-slate-900 border border-slate-300">{children}</td>
  </tr>
);

const Dash = () => <span className="text-slate-400">—</span>;

// 表頭：Firm Name 是可編輯答案（預設 PKF-CAP LLP），客戶名／年結引用涵蓋案。
const Qc35Header = ({ engagements = [], answers, set, readOnly, headerItems }) => {
  // The client name as the engagement recorded it; "(now: …)" when the client
  // master has been renamed since (docs/data-model.md §2). Under it the
  // engagementTag (service, number, serviceDescription) — one company may
  // hold several engagements for one FYE.
  const names = engagements
    .map((e) => {
      const p = parseEngagementName(e.engagementName || '');
      const service = e.serviceProvided !== undefined && e.serviceProvided !== null ? e.serviceProvided : p.service;
      const seq = e.engagementSeq !== undefined && e.engagementSeq !== null ? e.engagementSeq : p.seq;
      return { name: e.entityName || e.engagementName, now: e.entityNameNow || null, tag: engagementTag(service, seq, e.serviceDescription) };
    })
    .filter((n) => n.name)
    .map((n, i) => (
      <span key={i}>
        <NowName value={n.name} now={n.now} />
        {n.tag && <span className={coverageMeta}>{n.tag}</span>}
      </span>
    ));
  const primary = engagements[0] || null;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-xs font-semibold tracking-widest text-slate-500">PKF-CAP</div>
          <h3 className="text-base font-semibold text-slate-900">File Archival Form</h3>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="text-sm font-semibold text-slate-700">FORM QC35 (Revised)</div>
          <QcTipAllToggle />
        </div>
      </div>
      <table className="w-full border-collapse">
        <tbody>
          {headerItems.map((item) => (
            <HeaderRow key={item.id} label={item.label}>
              {readOnly ? (
                fmtQc35AnswerValue(item, answers[item.id])
              ) : (
                <>
                  <input
                    type="text"
                    value={answers[item.id] || ''}
                    onChange={(e) => set(item.id, e.target.value)}
                    className={inputCls}
                  />
                  <FieldInvalidInline field={`answers.${item.id}`} />
                  <FieldCommentInline field={`answers.${item.id}`} />
                </>
              )}
            </HeaderRow>
          ))}
          <HeaderRow label="Client Name">
            {names.length === 0 ? <span className="text-slate-400">— pick the engagement(s) first</span>
              : names.length === 1 ? names[0]
                : <ul className="space-y-0.5">{names.map((n, i) => <li key={i}>{n}</li>)}</ul>}
          </HeaderRow>
          <HeaderRow label="Reporting Year-End">
            {primary && primary.yearEnd ? formatDate(primary.yearEnd) : <Dash />}
          </HeaderRow>
        </tbody>
      </table>
    </div>
  );
};

// Read-only value with the optional struck-through previous answer (after a
// return) and a per-field extra (Detail hangs its comment anchor there).
const ReadValue = ({ item, value, previous, extra }) => {
  const fmt = (v) => (item.type === 'date' && v ? formatDate(v) : fmtQc35AnswerValue(item, v));
  const now = fmt(value);
  const before = previous === undefined ? null : fmt(previous);
  const changed = before !== null && before !== now;
  return (
    <div>
      <div className="text-sm text-slate-800 break-words whitespace-pre-line">
        {changed && <span className="line-through text-slate-400 mr-2">{before}</span>}
        <span className={changed ? 'font-medium' : ''}>{now}</span>
      </div>
      {extra ? extra(item) : null}
    </div>
  );
};

// A row the bank pinned (no EQR ⇒ "Not applicable"): the value is the
// bank's, not the submitter's — QcForm's LockedValue look.
const LockedValue = ({ value, note }) => (
  <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm">
    <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" title={note || 'Filled in automatically'} />
    <span className="font-medium text-slate-700">{value === undefined || value === null || String(value).trim() === '' ? '—' : String(value)}</span>
    {note && <span className="text-xs text-slate-500">{note}</span>}
  </div>
);

// 數字＋單位後綴（% 的三題與 EQR 的小時）走 FormKit 的 UnitInput：單位印在框裡，
// 表格窄、也不必再把單位寫進題目。
const EditControl = ({ item, value, onChange }) => {
  switch (item.type) {
    case 'percent': {
      // A percentage outside 0–100 is said the moment it is typed — the
      // Submit-time marking (formValidation) is for blanks, not for a value
      // that is visibly wrong while the cursor is still in the box.
      const rangeError = qc35PercentError(item, value);
      return (
        <>
          <UnitInput value={value} onChange={onChange} unit="%" step={0.1} max={100} />
          {rangeError && <p className="text-xs text-red-600 mt-1">{rangeError}</p>}
        </>
      );
    }
    case 'hours':
      return <UnitInput value={value} onChange={onChange} unit="hours" step={0.5} />;
    case 'date':
      return <input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className={`${inputCls} max-w-[14rem]`} />;
    case 'textarea':
      return <textarea rows="2" value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
    default:
      return <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  }
};

// A `derived` row: the value belongs to the covered engagement, so it reads
// the same whoever is looking and whatever the form state is. The note says
// where it comes from — otherwise a read-only row in an editable table just
// looks broken.
const DerivedValue = ({ item, engagement }) => {
  const value = qc35DerivedValue(item, engagement);
  return (
    <div>
      <div className="text-sm text-slate-800">
        {value ? (item.type === 'date' ? formatDate(value) : fmtQc35AnswerValue(item, value))
          : <span className="text-slate-400">— no approved Signed Financial Statements on record yet</span>}
      </div>
      <p className="text-xs text-slate-500 mt-0.5">From the Signed Financial Statements — not entered here.</p>
    </div>
  );
};

// The guideline that applies to a time-involvement row for THIS engagement
// (the bank reads the QC context: High risk 5% / 13 hours, otherwise 3% …).
const GuidelineNote = ({ item, ctx }) => {
  const text = qc35GuidelineOf(item, ctx);
  return text ? <p className="text-xs text-slate-500 mt-1">{text}</p> : null;
};

// One part of a two-part cell (the value, or its Comments box).
const Part = ({ item, answers, set, readOnly, previousAnswers, extra, ctx, caption, required }) => {
  const invalid = useFieldInvalid(`answers.${item.id}`);
  return (
    <div>
      <div className={subCaption(invalid)}>{caption}{required && !readOnly ? ' *' : ''}</div>
      {readOnly ? (
        <ReadValue item={item} value={answers[item.id]} previous={previousAnswers ? previousAnswers[item.id] : undefined} extra={extra} />
      ) : (
        <>
          <EditControl item={item} value={answers[item.id]} onChange={(v) => set(item.id, v)} />
          <FieldInvalidInline field={`answers.${item.id}`} />
          <FieldCommentInline field={`answers.${item.id}`} />
        </>
      )}
      {item.guideline ? <GuidelineNote item={item} ctx={ctx} /> : null}
    </div>
  );
};

const ItemRow = ({ item, answers, set, readOnly, previousAnswers, extra, engagement, ctx }) => {
  const required = isQc35ItemRequired(item, answers, ctx);
  const invalid = useFieldInvalid(`answers.${item.id}`);
  const derived = isQc35DerivedItem(item);
  const locked = isQc35ItemLocked(item, answers, ctx);
  const comment = qc35CommentItemOf(item);
  const partProps = { answers, set, readOnly, previousAnswers, extra, ctx };
  return (
    <tr className={locked ? 'bg-slate-50/60' : ''}>
      <td className={cellLabel}>
        <span className={invalid && !derived ? fieldInvalidLabel : undefined}>{item.label}{required && !readOnly && !derived ? ' *' : ''}</span>
        <QcTip tip={item.tip} />
      </td>
      <td className={`${cellValue}${comment && !locked ? ' space-y-2' : ''}`}>
        {derived ? (
          <DerivedValue item={item} engagement={engagement} />
        ) : locked ? (
          // The whole row does not apply — the bank pinned its value; no
          // empty Comments box to wonder about (QcForm's rowNa).
          <div>
            {readOnly
              ? <ReadValue item={item} value={answers[item.id]} previous={previousAnswers ? previousAnswers[item.id] : undefined} extra={extra} />
              : <LockedValue value={answers[item.id]} note="From the QC6 / QC7 form — no reviewer is named" />}
            {!readOnly && <FieldCommentInline field={`answers.${item.id}`} />}
            <GuidelineNote item={item} ctx={ctx} />
          </div>
        ) : comment ? (
          <>
            <Part item={item} caption="Response" required={required} {...partProps} />
            <Part item={comment} caption="Comments" required={isQc35ItemRequired(comment, answers, ctx)} {...partProps} />
          </>
        ) : readOnly ? (
          <ReadValue item={item} value={answers[item.id]} previous={previousAnswers ? previousAnswers[item.id] : undefined} extra={extra} />
        ) : (
          <>
            <EditControl item={item} value={answers[item.id]} onChange={(v) => set(item.id, v)} />
            <FieldInvalidInline field={`answers.${item.id}`} />
            <FieldCommentInline field={`answers.${item.id}`} />
          </>
        )}
      </td>
    </tr>
  );
};

const Layout = ({ engagements, rowProps }) => {
  const items = getQc35Items();
  const headerItems = items.filter((i) => i.header);
  // 條件列（回收率 <30% 的說明）不成立就整列不出現——業主拍板「未達時才出現」。
  // Comments 列不自己佔一列：它畫在母題的 Response 格裡。
  const tableItems = items.filter((i) => !i.header && !i.commentFor && isQc35ItemShown(i, rowProps.answers));
  // 衍生列讀涵蓋案——QC35 一單一公司，primary 就是那一間（legacy 多案單也只
  // 有第一間有即時列可讀）。
  const primaryEngagement = engagements[0] || null;
  return (
    <QcTipProvider>
      <div className="space-y-4">
        <Qc35Header
          engagements={engagements}
          answers={rowProps.answers}
          set={rowProps.set}
          readOnly={rowProps.readOnly}
          headerItems={headerItems}
        />
        <table className="w-full border-collapse table-fixed">
          <thead>
            <tr>
              <th className={colHead}>Checklist — Description</th>
              <th className={colHead}>Response · Comments</th>
            </tr>
          </thead>
          <tbody>
            {tableItems.map((item) => (
              <ItemRow key={item.id} item={item} {...rowProps} engagement={primaryEngagement} />
            ))}
          </tbody>
        </table>
        {/* 時間投入三題的判準不在表上（QMM 才有）——指引圖就接在表格下方，
            填的人與審核的人都在同一頁看得到，並印出這一案用的評級與 EQR。 */}
        <Qc35TimeGuideline ctx={rowProps.ctx} hasEngagement={!!primaryEngagement} />
      </div>
    </QcTipProvider>
  );
};

/**
 * Editable checklist. Every change runs through applyQc35AutoAnswers with the
 * engagement's QC context; `onContext(ctx)` reports that context to the page
 * (null while unknown) so its gap list applies the same rules.
 */
export const Qc35ChecklistForm = ({ answers, onChange, engagements = [], onContext = null }) => {
  const primary = engagements[0] || null;
  const ctx = useQc35Context(primary ? primary.id : null);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onContextRef = useRef(onContext);
  onContextRef.current = onContext;
  // Sync the derived values on mount and whenever the QC context arrives or
  // changes (never on typing): the firm-name default, any conditional row
  // left over from a stale condition, and the N/A pin on the EQR row.
  useEffect(() => {
    if (onContextRef.current) onContextRef.current(ctx);
    const cur = answersRef.current || {};
    const next = applyQc35AutoAnswers(cur, ctx);
    const keys = new Set([...Object.keys(next), ...Object.keys(cur)]);
    if ([...keys].some((k) => next[k] !== cur[k])) onChangeRef.current(next);
  }, [ctx]);

  const a = useMemo(() => answers || {}, [answers]);
  const set = (id, v) => onChange(applyQc35AutoAnswers({ ...a, [id]: v }, ctx));
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <Layout engagements={engagements} rowProps={{ answers: a, set, readOnly: false, ctx }} />
    </div>
  );
};

/**
 * Read-only checklist. `previousAnswers` (optional) shows the answer before
 * the request was returned, struck through; `extra(item)` renders under every
 * value (Detail puts its comment anchor there). The guideline notes read the
 * engagement's CURRENT QC context, so a reviewer sees the rule as it stands.
 */
export const Qc35ChecklistView = ({ answers, engagements = [], previousAnswers = null, extra = null }) => {
  const primary = engagements[0] || null;
  const ctx = useQc35Context(primary ? primary.id : null);
  const a = useMemo(() => answers || {}, [answers]);
  return (
    <Layout
      engagements={engagements}
      rowProps={{ answers: a, set: () => {}, readOnly: true, previousAnswers, extra, ctx }}
    />
  );
};

export default Qc35ChecklistForm;
